import { createFileRoute } from "@tanstack/react-router";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy — Understand Your Genome" },
      {
        name: "description",
        content: "Who runs this, what happens to your file, and what rights you have over it.",
      },
    ],
  }),
  component: PrivacyPage,
});

// Bump this string by hand whenever the actual policy text below changes —
// deliberately not `new Date()` at render time, which would silently claim
// "just updated" on every single page load instead of reflecting a real edit.
const PRIVACY_POLICY_LAST_UPDATED = "September 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl">{title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14">
        <h1 className="text-3xl">Privacy</h1>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          This page says plainly who runs this, what happens to your file, and what you can do
          about it. No legal filler — if something here isn't clear, say so via the contact address
          below and we'll fix the wording.
        </p>

        <Section title="Who runs this">
          <p>
            Understand Your Genome is run by Charles Imbusch as a private individual — not a
            company. For anything about privacy, your data, or this policy, write to{" "}
            <a href="mailto:contact@understandyourgenome.org" className="text-primary underline underline-offset-2">
              contact@understandyourgenome.org
            </a>
            .
          </p>
        </Section>

        <Section title="What we collect and why">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong className="text-foreground">Your email</strong>, to create your account and sign
              you in.
            </li>
            <li>
              <strong className="text-foreground">The genetic file you upload</strong> (VCF, 23andMe
              export, or any other VCF provider) and the annotations we generate from it — this is
              why the service exists.
            </li>
            <li>
              <strong className="text-foreground">Nothing else, by default.</strong> If you turn on
              email notifications, we use your email to tell you when a file is done processing —
              nothing more.
            </li>
            <li>
              If you send us feedback, we keep the message and, if you chose to give one, the email
              you provided with it.
            </li>
          </ul>
        </Section>

        <Section title="Your genetic data specifically">
          <p>
            Genetic data is about as personal as data gets, and we treat it that way. Processing it
            requires your explicit go-ahead — the checkbox you tick before uploading — and it's used
            for exactly one thing: producing your annotated report. It is never used to train any
            model, never analyzed in aggregate, and never looked at by us unless you explicitly ask
            us to help you with something.
          </p>
          <p>
            Annotation itself runs entirely against reference databases (ClinVar, dbSNP, gnomAD,
            SNPedia, and others) that we download and keep on our own infrastructure. Your file is
            never sent to any third-party API or external service to be annotated — the comparison
            happens locally, against our own copy of that public data.
          </p>
        </Section>

        <Section title="Who can actually see it">
          <p>
            Only you, while signed into your own account. Access is enforced at the database level
            (row-level security), not just hidden in the interface — and this applies to
            administrators of this site too: admin accounts can see membership and job status for
            support purposes, but have no access path to the contents of anyone's results. That's a
            deliberate design choice, not a policy we're merely promising to follow.
          </p>
        </Section>

        <Section title="Where it's stored">
          <p>
            On infrastructure we run and control directly — not a third-party cloud genetics
            platform, not shared with data brokers, not sold, ever. Files are encrypted in storage.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            For as long as you want. Delete a single upload from your dashboard, or wipe everything
            at once from{" "}
            <span className="text-foreground">Account &amp; your data</span> — either way it's a real
            deletion of the underlying file and results, not a hide switch. If you'd like your
            account itself closed entirely, email{" "}
            <a href="mailto:contact@understandyourgenome.org" className="text-primary underline underline-offset-2">
              contact@understandyourgenome.org
            </a>{" "}
            and we'll take care of it.
          </p>
        </Section>

        <Section title="What we don't do">
          <ul className="list-disc space-y-2 pl-5">
            <li>No analytics or tracking cookies — we don't run any third-party analytics at all.</li>
            <li>No advertising, and no marketing emails beyond what you've explicitly asked for.</li>
            <li>No automated decisions made about you — everything here is informational, for you to read and act on (or not) as you choose.</li>
          </ul>
        </Section>

        <Section title="Your rights">
          <p>
            Whatever the specifics of the law where you live, we'll honor the same baseline: you can
            ask what we hold about you, correct it, get a copy of it, or have it deleted — most of
            which you can already do yourself from your account page. For anything else, email{" "}
            <a href="mailto:contact@understandyourgenome.org" className="text-primary underline underline-offset-2">
              contact@understandyourgenome.org
            </a>
            .
          </p>
        </Section>

        <Section title="Who this is for">
          <p>
            This service isn't intended for anyone under 18 — genetic results deserve the judgment
            of an adult, ideally alongside a doctor or genetic counselor for anything that raises a
            real question.
          </p>
        </Section>

        <Section title="Changes to this page">
          <p>
            If this policy changes in a way that matters, we'll say so here with a visible date. This
            version: {PRIVACY_POLICY_LAST_UPDATED}.
          </p>
        </Section>
      </main>
      <SiteFooter />
    </div>
  );
}
